import React from 'react';
import { Edit2, Layers, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { HsmContact, HsmContactSortKey } from '../../types';
import { getSortIcon, getAriaSort, getContactStatusBadge } from './contactHelpers';

export interface ContactsTableProps {
  contacts: HsmContact[];
  loading: boolean;
  sortColumn: HsmContactSortKey;
  sortDirection: 'asc' | 'desc';
  isAdmin: boolean;
  onToggleSort: (column: HsmContactSortKey) => void;
  onEdit: (contact: HsmContact) => void;
  onAssignBatch: (contact: HsmContact) => void;
  onDelete: (contactId: number) => void;
}

export function ContactsTable({
  contacts,
  loading,
  sortColumn,
  sortDirection,
  isAdmin,
  onToggleSort,
  onEdit,
  onAssignBatch,
  onDelete,
}: ContactsTableProps) {
  return (
    <div className="hidden md:block overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead aria-sort={getAriaSort('phoneNumber', sortColumn, sortDirection)}>
              <button
                type="button"
                onClick={() => onToggleSort('phoneNumber')}
                className="flex items-center gap-1"
              >
                Telefone
                {getSortIcon(sortColumn === 'phoneNumber', sortDirection)}
              </button>
            </TableHead>
            <TableHead aria-sort={getAriaSort('name', sortColumn, sortDirection)}>
              <button
                type="button"
                onClick={() => onToggleSort('name')}
                className="flex items-center gap-1"
              >
                Nome
                {getSortIcon(sortColumn === 'name', sortDirection)}
              </button>
            </TableHead>
            <TableHead>Lote</TableHead>
            <TableHead aria-sort={getAriaSort('status', sortColumn, sortDirection)}>
              <button
                type="button"
                onClick={() => onToggleSort('status')}
                className="flex items-center gap-1"
              >
                Status
                {getSortIcon(sortColumn === 'status', sortDirection)}
              </button>
            </TableHead>
            <TableHead
              className="hidden lg:table-cell"
              aria-sort={getAriaSort('createdAt', sortColumn, sortDirection)}
            >
              <button
                type="button"
                onClick={() => onToggleSort('createdAt')}
                className="flex items-center gap-1"
              >
                Criado em
                {getSortIcon(sortColumn === 'createdAt', sortDirection)}
              </button>
            </TableHead>
            <TableHead className="text-right">Acoes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
              </TableCell>
            </TableRow>
          ) : contacts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-sm">
                Nenhum contato encontrado.
              </TableCell>
            </TableRow>
          ) : (
            contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="font-medium">{contact.phoneNumber}</TableCell>
                <TableCell>{contact.name || '-'}</TableCell>
                <TableCell>
                  {contact.batchName ? (
                    <Badge variant="outline">{contact.batchName}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>{getContactStatusBadge(contact.status)}</TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                  {new Date(contact.createdAt).toLocaleDateString('pt-BR')}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(contact)}
                      title="Editar contato"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAssignBatch(contact)}
                      title="Atribuir lote"
                    >
                      <Layers className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(contact.id)}
                        title="Excluir contato"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
